// What? A React component that manages the multi-step document type creation/editing process.
// Why? To provide a structured wizard interface for defining document types with validation and state management.
// How? The component's logic flow:
// 1. Step Management: Handle navigation between Basic Info, Templates, Source Documents, Data Fields, and Structure tabs
// 2. Validation: Validate each step before allowing progression
// 3. State Synchronization: Save changes when navigating between steps
// 4. Data Loading: Load existing data when editing a document type
// 5. Form Submission: Collect and submit all document type data

import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  DialogTitle,
  Modal,
  ModalDialog,
  Stack,
  Step,
  StepButton,
  Stepper,
  Typography,
} from "@mui/joy";
import { Check as CheckIcon, Error as ErrorIcon } from "@mui/icons-material";

import Loading from "../../../../components/Loading";
import PromptRefinementModal from "../common/PromptRefinementModal";
import { TabDefinition } from "../../../../types/tabs";
import { useFetchDocumentType } from "../../hooks/useDocumentTypes";
import { useDocumentType } from "./context/DocumentTypeContext";
import {
  useTutorialTarget,
  useTutorialHint,
} from "../../../../components/Tutorial";
import { useValidation } from "../../contexts/ValidationContext";
import type {
  DocumentTypeFormData,
  DocumentTypeState,
  DocumentTypeSection,
  DocumentTypeInput,
  SourceDocumentDefinition,
} from "../../types/documentTypes";
import { UploadedRefinementData } from "./tabs/StructureTab";
import BasicInfoTab from "./tabs/BasicInfoTab";
import SourceDocumentsTab from "./tabs/SourceDocumentsTab";
import DataFieldsTab from "./tabs/DataFieldsTab";
import StructureTab from "./tabs/StructureTab";
import TemplatesTab from "./tabs/TemplatesTab";

type DocumentTypeStep =
  | "basic"
  | "templates"
  | "source"
  | "fields"
  | "structure";

type ValidationError = string;

interface DocumentTypeManagerProps {
  steps: TabDefinition[];
  stepConfigs: Record<
    string,
    { getData: (state: DocumentTypeState) => Partial<DocumentTypeFormData> }
  >;
  onSubmit: (formData: DocumentTypeFormData) => Promise<void>;
  onCancel: () => void;
  onStepChange: (
    currentStep: string,
    formData: Partial<DocumentTypeFormData>,
  ) => Promise<void>;
  isSubmitting: boolean;
  documentTypeId?: number;
  workspace_id: string;
  loadInputFields: (documentTypeId: number) => Promise<DocumentTypeInput[]>;
  loadSections: (documentTypeId: number) => Promise<DocumentTypeSection[]>;
  loadSources: (documentTypeId: number) => Promise<SourceDocumentDefinition[]>;
  isFirstTimeCreation?: boolean;
}

const DocumentTypeManager: React.FC<DocumentTypeManagerProps> = ({
  steps,
  stepConfigs,
  onSubmit,
  onCancel,
  onStepChange,
  isSubmitting,
  documentTypeId,
  workspace_id,
  loadInputFields,
  loadSections,
  loadSources,
  isFirstTimeCreation = false,
}) => {
  const { state, dispatch } = useDocumentType();
  // Fetch the document type's Basic Info independently of the active tab so its
  // Name/Description/CustomInstructions are populated even when the wizard is
  // opened directly on a later step (see the load effect below).
  const { documentType: loadedDocumentType } = useFetchDocumentType(
    documentTypeId || 0,
    workspace_id,
    undefined,
    "Draft",
  );
  const {
    setShowValidation,
    getErrorMessages,
    validateFields,
    showValidation,
  } = useValidation();
  const [stepChanges, setStepChanges] = useState<Record<string, boolean>>({
    basic: false,
    templates: false,
    source: false,
    fields: false,
    structure: false,
  });
  const [apiError, setApiError] = useState<string | null>(null);
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const [isLoadingSections, setIsLoadingSections] = useState(false);
  const [hasLoadedSections, setHasLoadedSections] = useState(false);
  const [hasLoadedFields, setHasLoadedFields] = useState(false);
  const [fieldsLoadFailed, setFieldsLoadFailed] = useState(false);
  // True from the moment hasLoadedFields is reset until the refetch effect's
  // dispatch lands -- covers the render(s) before isLoadingFields itself
  // flips true, so the fields step can't be edited/navigated with stale,
  // ID-less data in that window (see PR #1233 review). Released on load
  // failure so a failed fetch doesn't leave the step stuck behind a spinner.
  const isFieldsRefreshPending =
    Boolean(documentTypeId) && !hasLoadedFields && !fieldsLoadFailed;
  const [isLoadingSourcesData, setIsLoadingSourcesData] = useState(false);
  const [hasLoadedSources, setHasLoadedSources] = useState(false);
  const [hasLoadedBasicInfo, setHasLoadedBasicInfo] = useState(false);
  const [isBulkRefineModalOpen, setIsBulkRefineModalOpen] = useState(false);
  const [uploadedBulkRefinementData, setUploadedBulkRefinementData] =
    useState<UploadedRefinementData | null>(null);
  const [selectedBulkGeneratedDocId, setSelectedBulkGeneratedDocId] = useState<
    number | null
  >(null);
  const [activeStep, setActiveStep] = useState(() =>
    steps.findIndex((t: TabDefinition) => t.id === state.currentTab),
  );

  useEffect(() => {
    const newActiveStep = steps.findIndex(
      (t: TabDefinition) => t.id === state.currentTab,
    );
    setActiveStep(newActiveStep);
  }, [state.currentTab, steps]);

  useEffect(() => {
    const loadFields = async () => {
      const currentStepId = steps[activeStep].id;
      if (
        currentStepId === "fields" &&
        documentTypeId &&
        !isLoadingFields &&
        !hasLoadedFields &&
        // Without this the failed-load path would re-enter on every
        // isLoadingFields flip and retry forever.
        !fieldsLoadFailed
      ) {
        try {
          setIsLoadingFields(true);
          const fields = await loadInputFields(documentTypeId);
          dispatch({ type: "UPDATE_FIELDS", payload: fields });
          setHasLoadedFields(true);
        } catch (error) {
          console.error("Failed to load input fields:", error);
          setApiError("Failed to load input fields. Please try again.");
          setFieldsLoadFailed(true);
        } finally {
          setIsLoadingFields(false);
        }
      }
    };

    loadFields();
  }, [
    activeStep,
    steps,
    documentTypeId,
    loadInputFields,
    dispatch,
    isLoadingFields,
    hasLoadedFields,
    fieldsLoadFailed,
  ]);

  // Clear a previous failure once the user leaves the step, so returning to
  // Data Fields retries the load instead of staying stuck on the error state
  // for the lifetime of the manager. Cleared on exit rather than entry so it
  // can't race the load effect within the same commit.
  useEffect(() => {
    if (steps[activeStep].id !== "fields" && fieldsLoadFailed) {
      setFieldsLoadFailed(false);
    }
  }, [activeStep, steps, fieldsLoadFailed]);

  useEffect(() => {
    const loadSectionsData = async () => {
      const currentStepId = steps[activeStep].id;
      if (
        currentStepId === "structure" &&
        documentTypeId &&
        !isLoadingSections &&
        !hasLoadedSections
      ) {
        try {
          setIsLoadingSections(true);
          const sections = await loadSections(documentTypeId);
          dispatch({ type: "UPDATE_SECTIONS", payload: sections });
          setHasLoadedSections(true);
        } catch (error) {
          console.error("Failed to load sections:", error);
          setApiError("Failed to load sections. Please try again.");
        } finally {
          setIsLoadingSections(false);
        }
      }
    };

    loadSectionsData();
  }, [
    activeStep,
    steps,
    documentTypeId,
    loadSections,
    dispatch,
    isLoadingSections,
    hasLoadedSections,
  ]);

  // Load source-document placeholders when entering the Structure step so the
  // per-section source selector has data without first visiting the Source
  // Documents tab. Reset hasLoadedSources after a source save to refetch the
  // newly assigned SourceFieldIDs.
  useEffect(() => {
    const loadSourcesData = async () => {
      const currentStepId = steps[activeStep].id;
      if (
        currentStepId === "structure" &&
        documentTypeId &&
        !isLoadingSourcesData &&
        !hasLoadedSources
      ) {
        try {
          setIsLoadingSourcesData(true);
          const sources = await loadSources(documentTypeId);
          dispatch({
            type: "UPDATE_SOURCE_DOCUMENTS",
            payload: { documents: sources, deletedSourceIds: [] },
          });
          setHasLoadedSources(true);
        } catch (error) {
          console.error("Failed to load sources:", error);
          setApiError("Failed to load source documents. Please try again.");
        } finally {
          setIsLoadingSourcesData(false);
        }
      }
    };

    loadSourcesData();
  }, [
    activeStep,
    steps,
    documentTypeId,
    loadSources,
    dispatch,
    isLoadingSourcesData,
    hasLoadedSources,
  ]);

  // Load Basic Info (Name/Description/CustomInstructions) into context as soon
  // as the document type is fetched, regardless of the active step. The Basic
  // Info tab normally does this, but it only mounts on step 1 -- so opening the
  // wizard directly on a later step (e.g. right after creation) would leave
  // state.Name empty, making the final "Complete" PATCH send an empty Name
  // (rejected with 422) and blank out the Description/CustomInstructions.
  // Guarded on an empty state.Name so it never clobbers in-progress user edits.
  useEffect(() => {
    if (
      documentTypeId &&
      loadedDocumentType &&
      !hasLoadedBasicInfo &&
      state.Name === ""
    ) {
      dispatch({ type: "UPDATE_NAME", payload: loadedDocumentType.Name });
      dispatch({
        type: "UPDATE_DESCRIPTION",
        payload: loadedDocumentType.Description || "",
      });
      dispatch({
        type: "UPDATE_CUSTOM_INSTRUCTIONS",
        payload: (loadedDocumentType as any).CustomInstructions || "",
      });
      setHasLoadedBasicInfo(true);
      // stepChanges.basic intentionally NOT set -- this is a load, not an edit.
    }
  }, [
    documentTypeId,
    loadedDocumentType,
    hasLoadedBasicInfo,
    state.Name,
    dispatch,
  ]);

  const stepRef0 = useTutorialTarget("meridian-dt-step-basic");
  const stepRef1 = useTutorialTarget("meridian-dt-step-templates");
  const stepRef2 = useTutorialTarget("meridian-dt-step-source");
  const stepRef3 = useTutorialTarget("meridian-dt-step-fields");
  const stepRef4 = useTutorialTarget("meridian-dt-step-structure");
  const stepTutorialRefs = [stepRef0, stepRef1, stepRef2, stepRef3, stepRef4];

  useTutorialHint(
    {
      id: "meridian-dt-step-basic",
      targetId: "meridian-dt-step-basic",
      title: "Step 1: Basic Information",
      body: "Give your document type a name and optional description. The name should clearly describe what kind of document this template will produce.",
      placement: "bottom",
    },
    isFirstTimeCreation && activeStep === 0,
  );
  useTutorialHint(
    {
      id: "meridian-dt-step-templates",
      targetId: "meridian-dt-step-templates",
      title: "Step 2: Templates",
      body: "Optionally upload a Word (.docx) file as a layout template. The AI will populate the template's placeholders with generated content.",
      placement: "bottom",
    },
    isFirstTimeCreation && activeStep === 1,
  );
  useTutorialHint(
    {
      id: "meridian-dt-step-source",
      targetId: "meridian-dt-step-source",
      title: "Step 3: Source Documents",
      body: "Define the documents users must supply at generation time — for example, meeting notes, a contract, or a research paper to summarise.",
      placement: "bottom",
    },
    isFirstTimeCreation && activeStep === 2,
  );
  useTutorialHint(
    {
      id: "meridian-dt-step-fields",
      targetId: "meridian-dt-step-fields",
      title: "Step 4: Data Fields",
      body: "Add structured inputs that users fill in when creating a document — for example, a project name, a date, or a dropdown selection.",
      placement: "bottom",
    },
    isFirstTimeCreation && activeStep === 3,
  );
  useTutorialHint(
    {
      id: "meridian-dt-step-structure",
      targetId: "meridian-dt-step-structure",
      title: "Step 5: Document Structure",
      body: "Define the sections that make up the generated document. Each section has a title and a prompt that guides the AI in writing that section's content.",
      placement: "bottom",
    },
    isFirstTimeCreation && activeStep === 4,
  );

  const getCurrentFormData = (): Partial<DocumentTypeFormData> => {
    const currentStepId = steps[activeStep].id;
    switch (currentStepId) {
      case "basic":
        return {
          Name: state.Name,
          Description: state.Description,
          CustomInstructions: state.CustomInstructions,
        };
      case "source":
        return {
          document_type_sources: state.document_type_sources,
        };
      case "fields":
        return {
          document_type_inputs: state.document_type_inputs,
        };
      case "structure":
        return {};
      case "templates":
        return {}; // Templates don't need saving on step change
      default:
        return {};
    }
  };

  // Simplified step validation logic
  const validateStep = (stepId: DocumentTypeStep): boolean => {
    setShowValidation(true);

    switch (stepId) {
      case "basic":
        return validateFields([
          {
            fieldId: "documentType.Name",
            value: state.Name,
            validate: (value: string | undefined) =>
              !value?.trim() ? "Name is required" : undefined,
          },
        ]);

      case "source":
        return state.document_type_sources.every((doc, index) =>
          validateFields([
            {
              fieldId: `sourceDocument.${index}.Label`,
              value: doc.Label,
              validate: (value: string | undefined) =>
                !value?.trim() ? "Display Label is required" : undefined,
            },
          ]),
        );

      case "fields":
        return state.document_type_inputs.every((field, index) => {
          const validations: Array<{
            fieldId: string;
            value: string | undefined;
            validate: (
              value: string | undefined,
            ) => ValidationError | undefined;
          }> = [
            {
              fieldId: `field.${index}.Label`,
              value: field.Label,
              validate: (value: string | undefined) =>
                !value?.trim() ? "Label is required" : undefined,
            },
          ];

          if (field.InputType === "Selection") {
            validations.push({
              fieldId: `field.${field.InputFieldID || index}.Options`,
              value: field.Options || "",
              validate: (value: string | undefined) => {
                if (!value) return "At least one non-empty option is required";

                const options = value.split("|||");
                const emptyOptions = options.filter((opt) => !opt.trim());
                const nonEmptyOptions = options.filter((opt) => opt.trim());

                if (nonEmptyOptions.length === 0) {
                  return "At least one non-empty option is required";
                }

                if (emptyOptions.length > 0) {
                  return "All options must have a value";
                }

                return undefined;
              },
            });
          }

          return validateFields(validations);
        });

      case "structure":
        // TODO: Temporary disabled validation for structure tab, now we let Title be empty
        // return state.document_type_sections.every((section, index) =>
        //   validateFields([{
        //     fieldId: `section.${index}.title`,
        //     value: section.Title,
        //     validate: (value: string | undefined) => !value?.trim() ? 'Title is required' : undefined
        //   }])
        // );
        return true;

      case "templates":
        return true; // No validation needed

      default:
        return true;
    }
  };

  const handleNext = async () => {
    try {
      const currentStepId = steps[activeStep].id;

      // Validate current step
      const isValid = validateStep(currentStepId as DocumentTypeStep);
      if (!isValid) {
        return;
      }

      // Clear validation state since we passed validation
      setShowValidation(false);

      // Save changes if needed
      if (stepChanges[currentStepId as DocumentTypeStep]) {
        const currentFormData = getCurrentFormData();
        await onStepChange(currentStepId, currentFormData);
        setStepChanges((prev: Record<string, boolean>) => ({
          ...prev,
          [currentStepId]: false,
        }));
        // New/edited sources now have backend SourceFieldIDs -- force the
        // Structure step's source selector to refetch them.
        if (currentStepId === "source") {
          setHasLoadedSources(false);
        }
        // New fields now have backend InputFieldIDs -- force a refetch so
        // local state doesn't keep treating already-created fields as new
        // (which caused duplicate creates when revisiting this step).
        if (currentStepId === "fields") {
          setHasLoadedFields(false);
          setFieldsLoadFailed(false);
        }
      }

      if (currentStepId === "structure") {
        await handleSubmit();
        return;
      }

      // Navigate to next step
      const nextStep = activeStep + 1;
      if (nextStep < steps.length) {
        setActiveStep(nextStep);
        dispatch({ type: "SET_TAB", payload: steps[nextStep].id });
      }
    } catch (err: any) {
      // Handle the error gracefully without crashing the application
      console.error("Failed to proceed:", err);

      // Display the backend error message in the modal
      // Navigation is automatically prevented because the error prevents reaching the navigation code
      const errorMessage =
        err.response?.detail ||
        err.message ||
        "Failed to save changes. Please try again.";
      setApiError(errorMessage);
    }
  };

  const handleSubmit = async () => {
    // Validate current step before submission
    const currentStepId = steps[activeStep].id;
    if (!validateStep(currentStepId as DocumentTypeStep)) {
      return;
    }

    try {
      await onSubmit({
        Name: state.Name,
        Description: state.Description,
        CustomInstructions: state.CustomInstructions,
        document_type_sources: state.document_type_sources,
        document_type_inputs: state.document_type_inputs,
      });
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        "Failed to save document type. Please try again.";
      setApiError(errorMessage);
      console.error("Failed to submit:", err);
    }
  };

  // Simplified error getter for step navigation
  const getStepErrors = (stepId: string): string[] => {
    const step = steps.find((t: TabDefinition) => t.id === stepId);
    if (!step?.errorPattern) return [];
    return getErrorMessages(step.errorPattern);
  };

  const handleBulkRefinementSuccess = () => {
    // TODO: Trigger data refresh. This needs to be handled by the parent component
    // (e.g., ManageDocumentType.tsx) that renders DocumentTypeManager
    // and controls the overall data fetching/state.
    // Example: props.onDataRefresh();
  };

  useEffect(() => {
    if (!isBulkRefineModalOpen) {
      setUploadedBulkRefinementData(null);
      setSelectedBulkGeneratedDocId(null);
    }
  }, [isBulkRefineModalOpen]);

  const getStepError = (stepId: string): boolean =>
    getStepErrors(stepId).length > 0;
  const getStepComplete = (stepId: string): boolean => {
    const idx = steps.findIndex((t: TabDefinition) => t.id === stepId);
    return idx < activeStep && !getStepError(stepId);
  };

  const handleStepClick = (idx: number) => {
    // Validate current step before allowing navigation
    const isValid = validateStep(steps[activeStep].id as DocumentTypeStep);
    if (!isValid) {
      setShowValidation(true);
      validateStep(steps[activeStep].id as DocumentTypeStep);
      return;
    }
    setShowValidation(false);

    if (stepChanges[steps[activeStep].id as DocumentTypeStep]) {
      const currentFormData = getCurrentFormData();
      const savedStepId = steps[activeStep].id;
      onStepChange(savedStepId, currentFormData)
        .then(() => {
          setStepChanges((prev: Record<string, boolean>) => ({
            ...prev,
            [savedStepId]: false,
          }));
          if (savedStepId === "source") {
            setHasLoadedSources(false);
          }
          if (savedStepId === "fields") {
            setHasLoadedFields(false);
            setFieldsLoadFailed(false);
          }
          setActiveStep(idx);
          dispatch({ type: "SET_TAB", payload: steps[idx].id });
        })
        .catch((err: any) => {
          // Handle the error gracefully without crashing the application
          console.error("Failed to save step changes:", err);

          // Display the backend error message in the modal
          // Navigation is automatically prevented because the error prevents reaching the navigation code
          const errorMessage =
            err.response?.detail ||
            err.message ||
            "Failed to save changes. Please try again.";
          setApiError(errorMessage);
        });
    } else {
      setActiveStep(idx);
      dispatch({ type: "SET_TAB", payload: steps[idx].id });
    }
  };

  const renderNavigationActions = () => (
    <Stack
      // Always horizontal: two short buttons fit side by side even on the
      // narrowest phone, and stacking them made each one span a full row
      // while the neighbouring Add Section button kept its natural width.
      direction="row"
      spacing={1}
      sx={{ alignItems: "center", flexWrap: "wrap" }}
    >
      <Button
        color="neutral"
        variant="outlined"
        onClick={() => {
          if (activeStep === 0) {
            onCancel();
          } else {
            const prevStep = activeStep - 1;
            handleStepClick(prevStep);
          }
        }}
        disabled={
          isSubmitting ||
          (steps[activeStep].id === "fields" &&
            (isLoadingFields || isFieldsRefreshPending))
        }
      >
        Back
      </Button>
      <Button
        variant="solid"
        color="primary"
        onClick={handleNext}
        disabled={
          isSubmitting ||
          (steps[activeStep].id === "fields" &&
            (isLoadingFields || isFieldsRefreshPending))
        }
      >
        {isSubmitting
          ? "Saving..."
          : activeStep === steps.length - 1
            ? "Complete"
            : "Next"}
      </Button>
    </Stack>
  );

  return (
    <>
      {/* overflow:visible on Card + CardContent allows the sticky header in
          StructureTab to anchor against the page scroll. Joy UI's Card sets
          overflow:hidden by default (to clip rounded corners), which traps
          position:sticky inside the Card and makes the header scroll with
          the page instead of pinning. */}
      <Card sx={{ overflow: "visible" }}>
        <CardContent sx={{ overflow: "visible" }}>
          {/* Step Navigation */}
          <Stepper
            sx={{
              mb: 3,
              "--Stepper-verticalGap": "0",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {steps.map((step, idx) => (
              <Step
                key={step.id}
                completed={getStepComplete(step.id)}
                indicator={
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      minWidth: 32,
                      minHeight: 32,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      bgcolor:
                        idx === activeStep
                          ? "primary.500"
                          : getStepComplete(step.id)
                            ? "success.500"
                            : "neutral.400",
                      color: getStepComplete(step.id) ? "#ffffff" : "white",
                      fontWeight: 600,
                      fontSize: "sm",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {getStepComplete(step.id) ? (
                      <CheckIcon fontSize="small" sx={{ color: "#ffffff" }} />
                    ) : (
                      idx + 1
                    )}
                  </Box>
                }
              >
                <span
                  ref={
                    idx < stepTutorialRefs.length
                      ? stepTutorialRefs[idx]
                      : undefined
                  }
                >
                  <StepButton
                    onClick={() => handleStepClick(idx)}
                    sx={{
                      flexDirection: "row",
                      gap: 1.5,
                      py: 1,
                      px: 2,
                      borderRadius: "md",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "transparent",
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{ fontWeight: idx === activeStep ? "lg" : "md" }}
                      >
                        {step.label}
                      </Box>
                      {getStepError(step.id) && (
                        <ErrorIcon color="error" sx={{ fontSize: 18 }} />
                      )}
                    </Stack>
                  </StepButton>
                </span>
              </Step>
            ))}
          </Stepper>

          {/* Tab Content */}
          {steps[activeStep].id === "basic" && (
            <BasicInfoTab
              name={state.Name}
              description={state.Description}
              customInstructions={state.CustomInstructions}
              documentTypeId={documentTypeId}
              onInitialLoad={({ name, description, customInstructions }) => {
                dispatch({ type: "UPDATE_NAME", payload: name });
                dispatch({ type: "UPDATE_DESCRIPTION", payload: description });
                dispatch({
                  type: "UPDATE_CUSTOM_INSTRUCTIONS",
                  payload: customInstructions,
                });
                // stepChanges.basic intentionally NOT set — this is a load, not a user edit
              }}
              onChange={({ name, description, customInstructions }) => {
                if (
                  name !== state.Name ||
                  description !== state.Description ||
                  customInstructions !== state.CustomInstructions
                ) {
                  dispatch({ type: "UPDATE_NAME", payload: name });
                  dispatch({
                    type: "UPDATE_DESCRIPTION",
                    payload: description,
                  });
                  dispatch({
                    type: "UPDATE_CUSTOM_INSTRUCTIONS",
                    payload: customInstructions,
                  });
                  setStepChanges((prev: Record<string, boolean>) => ({
                    ...prev,
                    basic: true,
                  }));
                  setApiError(null);
                }
              }}
              showValidation={showValidation}
            />
          )}
          {steps[activeStep].id === "templates" && (
            <TemplatesTab
              documentTypeId={documentTypeId}
              workspace_id={workspace_id}
              onChange={() => setApiError(null)}
            />
          )}
          {steps[activeStep].id === "source" && (
            <SourceDocumentsTab
              documents={state.document_type_sources}
              documentTypeId={documentTypeId}
              workspace_id={workspace_id}
              onChange={(docs, deletedIds) => {
                dispatch({
                  type: "UPDATE_SOURCE_DOCUMENTS",
                  payload: {
                    documents: docs,
                    deletedSourceIds: deletedIds || [],
                  },
                });
                setStepChanges((prev: Record<string, boolean>) => ({
                  ...prev,
                  source: true,
                }));
                setApiError(null);
              }}
              showValidation={showValidation}
            />
          )}
          {steps[activeStep].id === "fields" &&
            (isLoadingFields || isFieldsRefreshPending ? (
              <Loading />
            ) : fieldsLoadFailed ? (
              // Never render the editable form off a failed load: the local
              // fields would be stale/empty and missing InputFieldIDs, so
              // saving them would re-create duplicates.
              <Stack spacing={1.5} sx={{ py: 5, alignItems: "center" }}>
                <Typography level="title-md">
                  We couldn't load your data fields
                </Typography>
                <Typography
                  level="body-sm"
                  color="neutral"
                  sx={{ maxWidth: 420, textAlign: "center" }}
                >
                  This is usually temporary. Please try again in a moment.
                </Typography>
                <Button
                  variant="solid"
                  color="primary"
                  onClick={() => setFieldsLoadFailed(false)}
                  sx={{ mt: 0.5 }}
                >
                  Try again
                </Button>
              </Stack>
            ) : (
              <DataFieldsTab
                fields={state.document_type_inputs}
                documentTypeId={documentTypeId}
                workspace_id={workspace_id}
                onChange={(fields) => {
                  dispatch({ type: "UPDATE_FIELDS", payload: fields });
                  setStepChanges((prev: Record<string, boolean>) => ({
                    ...prev,
                    fields: true,
                  }));
                  setApiError(null);
                }}
                showValidation={showValidation}
              />
            ))}
          {steps[activeStep].id === "structure" && (
            <StructureTab
              sections={state.document_type_sections}
              documentTypeId={documentTypeId}
              documentTypeName={state.Name}
              dataFields={state.document_type_inputs}
              availableSources={state.document_type_sources}
              workspace_id={workspace_id}
              onChange={(sections) => {
                dispatch({ type: "UPDATE_SECTIONS", payload: sections });
                setStepChanges((prev: Record<string, boolean>) => ({
                  ...prev,
                  structure: true,
                }));
                setApiError(null);
              }}
              onChangeFunctional={(updater) => {
                dispatch({
                  type: "UPDATE_SECTIONS_FUNCTIONAL",
                  payload: updater,
                });
                setStepChanges((prev: Record<string, boolean>) => ({
                  ...prev,
                  structure: true,
                }));
                setApiError(null);
              }}
              showValidation={showValidation}
              navigationActions={renderNavigationActions()}
            />
          )}

          {/* Footer Action Buttons */}
          {steps[activeStep].id !== "structure" && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3,
                pt: 2,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              {renderNavigationActions()}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Error Modal */}
      <Modal open={!!apiError} onClose={() => setApiError(null)}>
        <ModalDialog>
          <DialogTitle>Error</DialogTitle>
          <Stack spacing={2}>
            <Typography>{apiError}</Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button
                variant="solid"
                color="primary"
                onClick={() => setApiError(null)}
              >
                OK
              </Button>
            </Box>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Bulk Refinement Modal */}
      {documentTypeId && isBulkRefineModalOpen && (
        <PromptRefinementModal
          isOpen={isBulkRefineModalOpen}
          onClose={() => setIsBulkRefineModalOpen(false)}
          documentTypeId={documentTypeId}
          onSuccess={() => {
            setIsBulkRefineModalOpen(false);
            handleBulkRefinementSuccess();
          }}
          uploadedRefinementData={uploadedBulkRefinementData}
          setUploadedRefinementData={setUploadedBulkRefinementData}
          selectedGeneratedDocId={selectedBulkGeneratedDocId}
          setSelectedGeneratedDocId={setSelectedBulkGeneratedDocId}
        />
      )}
    </>
  );
};

export default DocumentTypeManager;
